import { useState } from "react";
import { toast } from "sonner";
import { Users, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuickLinkSelect } from "@/components/fleet/QuickLinkSelect";

const Row = ({ r, label, excludeId, done, busy, onPick, testId }) => (
  <div className={`flex flex-col gap-1.5 border-b py-1.5 text-sm transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${done ? "bg-emerald-50" : ""}`} data-testid={testId}>
    <span><b>{r.plate}</b> · {label}</span>
    {done ? <span className="flex items-center gap-1 text-emerald-700" data-testid={`${testId}-done`}><CheckCircle2 className="h-4 w-4" />{done}</span>
      : <QuickLinkSelect name={r.name} excludeId={excludeId} busy={busy} onPick={onPick} testId={`${testId}-link`} />}
  </div>
);

const List = ({ title, rows, label, kind, ctx }) => rows.length > 0 && (
  <div className="mt-4" data-testid={`fleet-sync-${kind}`}>
    <div className="micro-label mb-1 border-b pb-1">{title} ({rows.length})</div>
    {rows.map((r) => <Row key={r.id} r={r} label={label(r)} excludeId={kind === "extra" ? r.employee_id : undefined} done={ctx.done[r.id]} busy={ctx.busy === r.id}
      onPick={(e) => ctx.assign(r, e)} testId={`fleet-sync-${kind}-${r.plate}`} />)}
  </div>
);

export const TeamSyncButton = ({ onDone }) => {
  const [res, setRes] = useState(null);
  const [lists, setLists] = useState(null);
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/fleet/sync-teams"); setRes(r.data); setLists(r.data); setDone({}); toast.success(`${r.data.teams} squadre allineate ai furgoni`); onDone?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const assign = async (row, emp) => {
    setBusy(row.id);
    try {
      const r = await api.post(`/fleet/vehicles/${row.id}/quick-assign`, { employee_id: emp.id });
      setRes(r.data); setDone((d) => ({ ...d, [row.id]: emp.name })); toast.success(`${row.plate} collegato a ${emp.name}`); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ctx = { done, busy, assign };
  return (
    <>
      <Button variant="outline" onClick={run} disabled={busy === true} data-testid="fleet-sync-teams"><Users className="mr-1 h-4 w-4" />Squadre dai furgoni</Button>
      <Dialog open={!!res} onOpenChange={(o) => !o && setRes(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="fleet-sync-report">
          <DialogHeader><DialogTitle className="font-display text-2xl">Squadre dai furgoni</DialogTitle></DialogHeader>
          {res && lists && <>
            <p className="text-sm">Ogni tecnico in forza con un furgone assegnato è una squadra (caposquadra il tecnico, furgone e magazzino furgone collegati). Collega qui sotto ogni mezzo al dipendente giusto: le squadre si aggiornano subito.</p>
            <p className="mt-2 font-mono text-sm" data-testid="fleet-sync-counts">{res.teams} squadre · {res.created} nuove · {res.updated} aggiornate · {res.archived} archiviate · {res.van_warehouses_created} magazzini furgone creati</p>
            <List title="Furgoni assegnati a tecnici cessati: da riassegnare" rows={lists.inactive} kind="inactive" label={(r) => r.name} ctx={ctx} />
            <List title="Tecnici con più furgoni: sposta il furgone in più su un altro dipendente" rows={lists.extra_vehicles} kind="extra" label={(r) => `${r.name} (squadra sul ${r.team_vehicle})`} ctx={ctx} />
            <List title="Nomi non collegati a un dipendente" rows={lists.unlinked} kind="unlinked" label={(r) => r.name} ctx={ctx} />
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
};
